import React, { useEffect, useState } from 'react';
import { CloseOutlined, SwapOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';

const FACE_STYLE = { backfaceVisibility: 'hidden', WebkitBackfaceVisibility: 'hidden' };

// Full-screen view of a card photo. With both sides it can be turned over.
const CardPhotoViewer = ({ front, back, onClose }) => {
  const { t } = useTranslation();
  const hasBoth = Boolean(front && back);
  const [showBack, setShowBack] = useState(!front && Boolean(back));

  const flip = () => hasBoth && setShowBack((v) => !v);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      else if (hasBoth && (e.key === ' ' || e.key === 'ArrowLeft' || e.key === 'ArrowRight')) {
        e.preventDefault();
        setShowBack((v) => !v);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [hasBoth, onClose]);

  const touchStartX = React.useRef(null);
  const onTouchEnd = (e) => {
    const start = touchStartX.current;
    touchStartX.current = null;
    if (start !== null && Math.abs(e.changedTouches[0].clientX - start) > 50) flip();
  };

  const face = (src, label, extraStyle) => (
    <img
      src={src}
      alt={label}
      draggable={false}
      style={{ ...FACE_STYLE, ...extraStyle }}
      className="absolute inset-0 w-full h-full object-contain rounded-xl bg-gray-900 shadow-2xl ring-1 ring-white/10"
    />
  );

  return (
    <div
      className="fixed inset-0 z-50 bg-black/90 flex flex-col items-center justify-center p-4 gap-4"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <button
        type="button"
        onClick={onClose}
        aria-label={t('businessCardScanner.close')}
        className="absolute top-4 end-4 w-11 h-11 rounded-full bg-white/20 text-white flex items-center justify-center hover:bg-white/30"
      >
        <CloseOutlined />
      </button>

      <div
        className="w-full max-w-md aspect-[7/4]"
        style={{ perspective: '1200px' }}
        onClick={(e) => {
          e.stopPropagation();
          flip();
        }}
        onTouchStart={(e) => {
          touchStartX.current = e.touches[0].clientX;
        }}
        onTouchEnd={onTouchEnd}
      >
        <div
          className="relative w-full h-full transition-transform duration-500 motion-reduce:transition-none"
          style={{ transformStyle: 'preserve-3d', transform: showBack ? 'rotateY(180deg)' : 'rotateY(0deg)' }}
        >
          {front && face(front, t('businessCardScanner.frontSide'))}
          {back && face(back, t('businessCardScanner.backSide'), { transform: 'rotateY(180deg)' })}
        </div>
      </div>

      {hasBoth && (
        <div className="flex flex-col items-center gap-3" onClick={(e) => e.stopPropagation()}>
          <div className="inline-flex rounded-full bg-white/15 p-1 text-sm font-medium text-white">
            <button
              type="button"
              onClick={() => setShowBack(false)}
              aria-pressed={!showBack}
              className={`px-4 py-1.5 rounded-full ${!showBack ? 'bg-white text-gray-900' : ''}`}
            >
              {t('businessCardScanner.frontSide')}
            </button>
            <button
              type="button"
              onClick={() => setShowBack(true)}
              aria-pressed={showBack}
              className={`px-4 py-1.5 rounded-full ${showBack ? 'bg-white text-gray-900' : ''}`}
            >
              {t('businessCardScanner.backShort')}
            </button>
          </div>
          <button type="button" onClick={flip} className="text-sm text-white/80 flex items-center gap-1.5">
            <SwapOutlined />
            {t('businessCardScanner.flip')}
          </button>
        </div>
      )}
    </div>
  );
};

export default CardPhotoViewer;
